// Helper functions (assuming these exist globally or are defined elsewhere)
function getElementNew(selector, context = document) {
  const el = context.querySelector(selector);
  if (!el) console.warn('Element not found for selector:', selector, context);
  return el;
}

function getElementsNew(selector, context = document) {
  const els = Array.from(context.querySelectorAll(selector));
  if (els.length === 0) console.warn('No elements found for selector:', selector, context);
  return els;
}

class imageZoom extends HTMLElement {
  constructor() {
    super();
    this.resizeTimeout = null;
    this.isResizing = false;
    this.isFirstClick = true; // Add flag for first click
    // Defer setup until connected to ensure DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.setupListeners.bind(this));
    } else {
        this.setupListeners();
    }
  }

  // Called when the element is added to the document
  connectedCallback() {
    // Setup might run via constructor's DOMContentLoaded, but run again if needed
    if (!this.listeners) {
        this.setupListeners();
    }
  }

  reload() {
    // Clean up old listeners if any exist before setting up new ones
    if (this.listeners) {
        this.listeners.forEach(({ target, type, handler }) => {
            target.removeEventListener(type, handler);
        });
    }
    this.setupListeners();
  }

  setupListeners() {
    this.listeners = new Set(); // Store listeners for cleanup

    // --- Main Selectors ---
    this.galleryWrapperEl = getElementNew('[data-module="media-gallery"]');
    if (!this.galleryWrapperEl) return; // Stop if the main wrapper isn't found

    this.gridViewEl = getElementNew('[data-el="media-gallery.grid-view"]', this.galleryWrapperEl);
    this.sliderViewEl = getElementNew('[data-el="media-gallery.slider-view"]', this.galleryWrapperEl);
    this.mediaCount = parseInt(this.galleryWrapperEl.dataset.mediaCount || 0);

    // --- Grid View Selectors ---
    this.gridItemEls = getElementsNew('[data-el="media-gallery.grid-item"]', this.gridViewEl);
    this.closeGridEl = getElementNew('[data-el="media-gallery.close-grid"]', this.gridViewEl);

    // --- Slider View Selectors ---
    this.itemEls = getElementsNew('[data-el="media-gallery.item"]', this.sliderViewEl); // Slider items
    this.closeSliderEl = getElementNew('[data-el="media-gallery.close-slider"]', this.sliderViewEl);
    this.imageEls = getElementsNew('[data-el="media-gallery.image"]', this.sliderViewEl); // Zoomable images in slider
    this.previousEl = getElementNew('[data-el="media-gallery.previous"]', this.sliderViewEl);
    this.nextEl = getElementNew('[data-el="media-gallery.next"]', this.sliderViewEl);
    this.trackEl = getElementNew('[data-el="media-gallery.track"]', this.sliderViewEl); // Slider track
    this.counterCurrentEl = getElementNew('[data-el="media-gallery.counterCurrent"]', this.sliderViewEl);

    // Initialize Flickity
    if (this.trackEl) {
      // Set initial height before Flickity initialization
      const viewportHeight = window.innerHeight;
      const minHeight = Math.min(viewportHeight * 0.8, 600);
      this.trackEl.style.minHeight = `${minHeight}px`;

      const flickityOptions = JSON.parse(this.trackEl.getAttribute('data-flickity') || '{}');
      this.flickity = new Flickity(this.trackEl, {
        ...flickityOptions,
        on: {
          ready: () => {
            // Set zoom image for the first slide when Flickity is ready
            const firstItem = this.itemEls[0];
            if (firstItem) {
              this.setZoomImage(firstItem);
              // Force initial resize after a short delay
              setTimeout(() => {
                if (this.flickity) {
                  this.flickity.resize();
                }
              }, 100);
            }
          },
          change: (index) => {
            this.updateCounter(index);
            const currentItem = this.itemEls[index];
            if (currentItem) {
              this.setZoomImage(currentItem);
              // Handle video playback
              const video = currentItem.querySelector('video');
              if (video) {
                video.play().catch(error => console.warn("Video autoplay failed:", error));
              }
              this.handleResize();
            }
          }
        }
      });

      // Resize Flickity when window resizes
      window.addEventListener('resize', () => this.handleResize());
    }

    // --- Event Listeners ---
    // Global listeners
    this.addManagedListener(document, 'keyup', this.handleKeyUp.bind(this));

    // Listeners to OPEN the grid gallery (adapt selectors as needed)
    getElementsNew('.product__media-item[data-index]').forEach(itemEl => {
        this.addManagedListener(itemEl, 'click', () => this.openGridGallery(parseInt(itemEl.dataset.index)));
    });
    getElementsNew('.prd-ProductImage_ZoomIcon').forEach(zoomIcon => {
      this.addManagedListener(zoomIcon, 'click', () => {
        const container = zoomIcon.closest('.product__media-item');
        const targetIndex = container ? parseInt(container.dataset.index) : 0;
        this.openGridGallery(targetIndex);
      });
    });

    // Grid View Listeners
    if (this.closeGridEl) {
        this.addManagedListener(this.closeGridEl, 'click', this.closeGallery.bind(this));
    }
    this.gridItemEls.forEach(gridItem => {
      this.addManagedListener(gridItem, 'click', () => this.openSliderView(parseInt(gridItem.dataset.index)));
    });

    // Slider View Listeners
    if (this.closeSliderEl) {
        this.addManagedListener(this.closeSliderEl, 'click', this.closeSliderView.bind(this));
    }
    this.imageEls.forEach(image => {
      this.addManagedListener(image, 'mousemove', this.zoomIn.bind(this));
      this.addManagedListener(image, 'mouseleave', (e) => { e.currentTarget.style.backgroundPosition = 'center'; });
    });
    if (this.previousEl) {
        this.addManagedListener(this.previousEl, 'click', () => this.flickity?.previous());
    }
    if (this.nextEl) {
        this.addManagedListener(this.nextEl, 'click', () => this.flickity?.next());
    }
  }

  // Helper to track listeners for easy removal
  addManagedListener(target, type, handler) {
      if (!target) return;
      target.addEventListener(type, handler);
      this.listeners.add({ target, type, handler });
  }

  handleKeyUp(e) {
      if (e.key === 'Escape') {
          if (this.sliderViewEl && this.sliderViewEl.style.display !== 'none') {
              this.closeSliderView(); // Close slider first if open
          } else {
              this.closeGallery(); // Otherwise close the whole gallery
          }
      }
      // Optional: Add ArrowLeft/ArrowRight listeners for slider nav if desired
      if (this.sliderViewEl && this.sliderViewEl.style.display !== 'none') {
        if (e.key === 'ArrowLeft' && this.previousEl) {
            this.onPreviousClick();
        } else if (e.key === 'ArrowRight' && this.nextEl) {
            this.onNextClick();
        }
      }
  }

  // Opens the initial Grid View
  openGridGallery(index = 0) { // index parameter is optional now, grid shows all
    if (!this.galleryWrapperEl || !this.gridViewEl || !this.sliderViewEl) return;
    this.galleryWrapperEl.setAttribute('aria-hidden', 'false');
    this.gridViewEl.style.display = ''; // Show grid
    this.sliderViewEl.style.display = 'none'; // Hide slider
    getElementNew('body').classList.add('util-SiteOverflowed');
    // Optional: Scroll grid view to show the initially clicked item if needed
  }

  // Closes the entire gallery (both views)
  closeGallery() {
      if (!this.galleryWrapperEl) return;
      this.galleryWrapperEl.setAttribute('aria-hidden', 'true');
      getElementNew('body').classList.remove('util-SiteOverflowed');
      // Optional: Reset slider state if needed when closing fully
      if (this.sliderViewEl) this.sliderViewEl.style.display = 'none';
      if (this.gridViewEl) this.gridViewEl.style.display = ''; // Ensure grid is default state
  }

  // Opens the Slider View from the Grid View
  openSliderView(index) {
    if (!this.gridViewEl || !this.sliderViewEl) return;
    
    // Set initial height before showing slider
    const viewportHeight = window.innerHeight;
    const minHeight = Math.min(viewportHeight * 0.8, 600);
    this.trackEl.style.minHeight = `${minHeight}px`;
    
    this.gridViewEl.style.display = 'none';
    this.sliderViewEl.style.display = '';

    // Wait for DOM update and initial resize
    requestAnimationFrame(() => {
      if (this.flickity) {
        // Force reinitialization on first click
        if (this.isFirstClick) {
          this.flickity.destroy();
          const flickityOptions = JSON.parse(this.trackEl.getAttribute('data-flickity') || '{}');
          this.flickity = new Flickity(this.trackEl, {
            ...flickityOptions,
            on: {
              ready: () => {
                const firstItem = this.itemEls[index];
                if (firstItem) {
                  this.setZoomImage(firstItem);
                  setTimeout(() => {
                    if (this.flickity) {
                      this.flickity.resize();
                    }
                  }, 100);
                }
              },
              change: (index) => {
                this.updateCounter(index);
                const currentItem = this.itemEls[index];
                if (currentItem) {
                  this.setZoomImage(currentItem);
                  const video = currentItem.querySelector('video');
                  if (video) {
                    video.play().catch(error => console.warn("Video autoplay failed:", error));
                  }
                  this.handleResize();
                }
              }
            }
          });
          this.isFirstClick = false;
          this.flickity.select(index, false);
        } else {
          this.flickity.select(index, false);
        }
        
        // Set zoom image for the selected slide
        const currentItem = this.itemEls[index];
        if (currentItem) {
          this.setZoomImage(currentItem);
        }

        // Force resize after DOM update
        setTimeout(() => {
          if (this.flickity) {
            this.flickity.resize();
          }
        }, 100);
      }
    });
  }

  // Closes the Slider View, returning to the Grid View
  closeSliderView() {
      if (!this.gridViewEl || !this.sliderViewEl) return;
      this.sliderViewEl.style.display = 'none'; // Hide slider
      this.gridViewEl.style.display = ''; // Show grid
      // Pause any playing video when closing slider?
      const currentItem = this.itemEls.find(item => item.getAttribute('aria-current') === 'true');
      if (currentItem) {
          const video = currentItem.querySelector('video');
          if (video) video.pause();
      }
  }

  zoomIn(e) {
      const zoom = e.currentTarget;
      if (!zoom || !zoom.style.backgroundImage || zoom.style.backgroundImage === 'none') return; // Don't zoom if no background image

      const rect = zoom.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      // Prevent default scroll behavior potentially caused by mousemove
      // e.preventDefault(); // uncomment if necessary

      const x = (offsetX / zoom.offsetWidth) * 100;
      const y = (offsetY / zoom.offsetHeight) * 100;

      zoom.style.backgroundPosition = `${x}% ${y}%`;
  }

  updateCounter(index) {
      if (this.counterCurrentEl) {
          this.counterCurrentEl.textContent = index + 1;
      }
  }

  goToImage(index, animate) {
      if (!this.itemEls || this.itemEls.length === 0 || !this.trackEl || !this.flickity) return;
      index = parseInt(index);
      if (isNaN(index)) index = 0;

      // Find the corresponding slider item
      const matchingItem = this.itemEls.find(item => parseInt(item.dataset.index) === index);
      if (!matchingItem) {
          console.warn(`Slider item with index ${index} not found.`);
          return;
      }

      // Pause video in previously active item
      const currentActiveItem = this.itemEls.find(item => item.getAttribute('aria-current') === 'true');
      if (currentActiveItem && currentActiveItem !== matchingItem) {
          currentActiveItem.setAttribute('aria-current', 'false');
          const currentVideo = currentActiveItem.querySelector('video');
          if (currentVideo) {
              currentVideo.pause();
              currentVideo.currentTime = 0;
          }
          // Reset zoom background
          const currentImageEl = currentActiveItem.querySelector('[data-el="media-gallery.image"]');
          if (currentImageEl) {
              currentImageEl.style.backgroundImage = '';
              currentImageEl.style.backgroundPosition = 'center';
          }
      }

      // Set new item as active
      matchingItem.setAttribute('aria-current', 'true');
      this.setZoomImage(matchingItem);

      // Play video if the new item is a video
      const newVideo = matchingItem.querySelector('video');
      if (newVideo) {
          newVideo.play().catch(error => console.warn("Video autoplay failed:", error));
      }

      // Use Flickity to select the slide
      this.flickity.select(index, animate);
      this.updateCounter(index);
  }

  onPreviousClick() {
      if (!this.itemEls || this.itemEls.length === 0) return;
      const current = this.itemEls.find(item => item.getAttribute('aria-current') === 'true');
      const currentIndex = current ? parseInt(current.dataset.index) : 0;
      let targetIndex = currentIndex - 1;

      if (targetIndex < 0) {
        targetIndex = this.mediaCount - 1; // Wrap around to the last item
      }

      this.goToImage(targetIndex, true); // Animate transition
  }

  onNextClick() {
      if (!this.itemEls || this.itemEls.length === 0) return;
      const current = this.itemEls.find(item => item.getAttribute('aria-current') === 'true');
      const currentIndex = current ? parseInt(current.dataset.index) : 0;
      let targetIndex = currentIndex + 1;

      if (targetIndex >= this.mediaCount) {
        targetIndex = 0; // Wrap around to the first item
      }

      this.goToImage(targetIndex, true); // Animate transition
  }

  setZoomImage(item) {
      const mediasEl = item.querySelector('[data-el="media-gallery.image"]');
      if (!mediasEl) return;

      // Clear previous background first
      mediasEl.style.backgroundImage = '';
      mediasEl.style.backgroundPosition = 'center';

      if (mediasEl.classList.contains('prd-ProductMediaModal_Medias-image')) {
          const imageSrc = mediasEl.dataset.src; // Use the data-src for high-res zoom
          if (imageSrc) {
              // Preload the image before setting it as background for smoother zoom
              const img = new Image();
              img.onload = () => {
                  mediasEl.style.backgroundImage = `url("${imageSrc}")`;
              }
              img.onerror = () => {
                  console.warn(`Failed to load high-res image: ${imageSrc}`);
              }
              img.src = imageSrc;
          } else {
              console.warn('No data-src found for zoom image on item:', item);
          }
      }
      // Videos don't need background image for zoom
  }

  // Add a new method to handle resizing
  handleResize() {
    if (!this.flickity || this.isResizing) return;
    
    this.isResizing = true;
    clearTimeout(this.resizeTimeout);
    
    this.resizeTimeout = setTimeout(() => {
      // Get the current active slide
      const currentItem = this.itemEls[this.flickity.selectedIndex];
      if (!currentItem) {
        this.isResizing = false;
        return;
      }

      // Set minimum height based on viewport
      const viewportHeight = window.innerHeight;
      const minHeight = Math.min(viewportHeight * 0.8, 600);
      this.trackEl.style.minHeight = `${minHeight}px`;

      // Check if it's a video
      const video = currentItem.querySelector('video');
      if (video) {
        // For videos, wait for metadata to be loaded
        if (video.readyState >= 1) {
          this.flickity.resize();
          this.isResizing = false;
        } else {
          video.addEventListener('loadedmetadata', () => {
            this.flickity.resize();
            this.isResizing = false;
          }, { once: true });
        }
        return;
      }

      // For images, check if they're loaded
      const images = currentItem.querySelectorAll('img');
      let loadedImages = 0;
      
      if (images.length === 0) {
        this.flickity.resize();
        this.isResizing = false;
        return;
      }

      images.forEach(img => {
        if (img.complete) {
          loadedImages++;
          if (loadedImages === images.length) {
            this.flickity.resize();
            this.isResizing = false;
          }
        } else {
          img.addEventListener('load', () => {
            loadedImages++;
            if (loadedImages === images.length) {
              this.flickity.resize();
              this.isResizing = false;
            }
          }, { once: true });
        }
      });
    }, 100);
  }

  // Optional: Clean up listeners when element is removed
  disconnectedCallback() {
      if (this.listeners) {
          this.listeners.forEach(({ target, type, handler }) => {
              if (target) {
                 target.removeEventListener(type, handler);
              }
          });
          this.listeners.clear();
          this.listeners = null;
      }
      
      // Clean up resize timeout
      clearTimeout(this.resizeTimeout);
      
      // Clean up Flickity
      if (this.flickity) {
          this.flickity.destroy();
          this.flickity = null;
      }
      
      // Reset first click flag
      this.isFirstClick = true;
      
      // Remove body class if gallery is open when disconnected
      getElementNew('body')?.classList.remove('util-SiteOverflowed');
  }
}